import React from 'react';

/** Indicador de carregamento para botões do módulo financeiro. */
export function FinSpinner({ size = '1.125rem' }: { size?: string }): React.ReactElement {
  return (
    <span
      className="fin-spinner"
      style={{ width: size, height: size }}
      role="status"
      aria-label="Carregando"
    />
  );
}

/** Título de campo com asterisco vermelho para obrigatórios. */
export function FinFieldTitle({
  children,
  required,
  htmlFor,
}: {
  children: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
}): React.ReactElement {
  const mark = required ? <span className="fin-required">*</span> : null;
  if (htmlFor) {
    return (
      <label className="fin-field-title" htmlFor={htmlFor}>
        <span className="fin-field-title__text">{children}</span>
        {mark}
      </label>
    );
  }
  return (
    <span className="fin-field-title">
      <span className="fin-field-title__text">{children}</span>
      {mark}
    </span>
  );
}

/** Valores negativos: hífen ASCII antes do símbolo da moeda (ex.: -R$ 690,00). */
export function formatBRL(n: number): string {
  if (n === 0 || Object.is(n, -0)) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(0);
  }
  const neg = n < 0;
  const formatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Math.abs(n));
  return neg ? `-${formatted}` : formatted;
}

export type CadastroItem = { id: number; name: string };

export type LancamentoSituacao = 'pendente' | 'pago' | 'atrasado';

export type Pagamento = {
  /** Vazio no envio (criação local); preenchido no retorno da API. */
  id?: number;
  valor: number;
  /** YYYY-MM-DD. */
  data: string;
  responsavel?: string;
  metodo?: string;
};

export type Lancamento = {
  id: string;
  tipo: 'entrada' | 'saida';
  data: string;
  descricao: string;
  cliente?: string;
  responsavel?: string;
  categoria?: string;
  planoDeContas?: string;
  metodo?: string;
  valor: number;
  /** Soma de pagamentos (cache). Derivado de `pagamentos` no envio. */
  valorPago: number;
  vencimento?: string;
  /** Data do último pagamento quando totalmente quitado (cache). */
  dataPagamento?: string;
  parcelaAtual?: number;
  totalParcelas?: number;
  /** Derivado no servidor (pendente | pago | atrasado). */
  situacao: LancamentoSituacao;
  anexo?: string;
  anexoUrl?: string;
  /** Apenas envio — não preenchido na listagem vinda da API. */
  anexoBase64?: string;
  anexoContentType?: string;
  /** Opcional no envio: "pago" força quitação. */
  situacaoEnvio?: 'pago';
  /**
   * Lista de pagamentos parciais. Quando enviada (não-undefined), substitui o histórico no servidor.
   * O servidor sempre devolve preenchido (pode ser []).
   */
  pagamentos?: Pagamento[];
};

export function PageShell({
  title,
  subtitle,
  children,
  actions,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="fin-page">
      <header className="fin-page__header">
        <div>
          <h1 className="fin-page__title">{title}</h1>
          {subtitle ? <p className="fin-page__subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="fin-page__actions">{actions}</div> : null}
      </header>
      {children}
    </div>
  );
}

export function FinCard({
  title,
  children,
  className = '',
  headerRight,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
  /** Conteúdo à direita do título (ex.: botões, contadores). */
  headerRight?: React.ReactNode;
}): React.ReactElement {
  return (
    <section className={`fin-card ${className}`.trim()}>
      <div className="fin-card__title-row">
        <h2 className="fin-card__title">{title}</h2>
        {headerRight ? <div className="fin-card__title-row-actions">{headerRight}</div> : null}
      </div>
      <div className="fin-card__body">{children}</div>
    </section>
  );
}
